<template>
  <div class="playback-container">
    <!-- TOP TOOLBAR / BRANCH SELECTOR -->
    <div class="top-control-bar">
      <!-- 1. Dropdown Branch Selector -->
      <div class="device-selector-wrapper">
        <label for="branchSelect" class="selector-label">
          <i class="ni ni-building text-info me-1"></i> Select Branch:
        </label>
        <select 
          id="branchSelect" 
          v-model="branchId" 
          @change="onBranchChange" 
          class="device-select-dropdown"
        >
          <option v-if="branchesLoading" disabled value="">Loading branches...</option>
          <option 
            v-for="branch in branchesList" 
            :key="branch.id" 
            :value="branch.id"
          >
            {{ branch.branch_name }} (ID: {{ branch.id }})
          </option>
        </select>
      </div>

      <!-- 2. Manual Branch ID Quick Input -->
      <div class="manual-input-wrapper">
        <label class="selector-label">Or Type ID:</label>
        <input 
          type="number" 
          min="1" 
          v-model.number="manualBranchId" 
          @keyup.enter="applyManualBranchId" 
          placeholder="ID#" 
          class="manual-id-input"
        />
        <button @click="applyManualBranchId" class="btn-apply-id" title="Load this Branch ID">
          Go
        </button>
      </div>

      <!-- Controls Overlay -->
      <div class="status-overlay">
        <span class="badge bg-gradient-info me-2">Active Branch: {{ branchId }}</span>
        <button @click="toggleFullscreen" class="btn-fullscreen" title="Toggle Fullscreen">
          <i class="ni ni-button-power"></i> Fullscreen
        </button>
      </div>
    </div>

    <!-- 1. EMERGENCY BROADCAST DISPLAY (PRIORITY 1) -->
    <div v-if="playType === 'emergency_broadcast' && playbackData" class="screen emergency-screen">
      <div class="emergency-banner">
        <i class="ni ni-notification-70 text-white pulse-icon"></i>
        <h1>EMERGENCY BROADCAST</h1>
      </div>

      <div class="emergency-content">
        <h2 v-if="playbackData.title" class="emergency-title">{{ playbackData.title }}</h2>
        <p v-if="playbackData.message" class="emergency-message">{{ playbackData.message }}</p>

        <!-- EMERGENCY PLAYLIST ITEMS CAROUSEL -->
        <div v-if="currentEmergencyItem" class="media-wrapper">
          <img 
            v-if="isImage(getItemPath(currentEmergencyItem))" 
            :src="getMediaUrl(getItemPath(currentEmergencyItem))" 
            class="media-element"
          />
          <video 
            v-else-if="isVideo(getItemPath(currentEmergencyItem))" 
            :src="getMediaUrl(getItemPath(currentEmergencyItem))" 
            autoplay 
            muted 
            @ended="nextEmergencyItem" 
            class="media-element"
          ></video>
          <!-- Handle YouTube inside emergency playlist -->
          <iframe 
            v-else-if="getYoutubeEmbedUrl(getItemPath(currentEmergencyItem))"
            :src="getYoutubeEmbedUrl(getItemPath(currentEmergencyItem))"
            frameborder="0"
            class="fullscreen-iframe"
          ></iframe>
        </div>

        <!-- FALLBACK TO SINGLE MEDIA URL IF NO PLAYLIST -->
        <div v-else-if="playbackData.media_url" class="media-wrapper">
          <img 
            v-if="isImage(playbackData.media_url)" 
            :src="getMediaUrl(playbackData.media_url)" 
            alt="Emergency Media" 
            class="media-element"
          />
          <video 
            v-else-if="isVideo(playbackData.media_url)" 
            :src="getMediaUrl(playbackData.media_url)" 
            autoplay 
            muted 
            loop 
            class="media-element"
          ></video>
        </div>
      </div>
    </div>

    <!-- 2. LIVE VIDEO DISPLAY (PRIORITY 2) -->
    <div v-else-if="playType === 'live_video' && playbackData" class="screen live-video-screen">
      <div class="live-badge">🔴 LIVE BRANCH BROADCAST</div>

      <!-- YouTube Embed -->
      <div v-if="playbackData.video_type === 'youtube' && getYoutubeEmbedUrl(playbackData.youtube_url)" class="video-wrapper">
        <iframe 
          :src="getYoutubeEmbedUrl(playbackData.youtube_url)" 
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
          allowfullscreen 
          class="fullscreen-iframe"
        ></iframe>
      </div>

      <!-- Uploaded Video -->
      <div v-else-if="playbackData.video_type === 'upload' && playbackData.video_file" class="video-wrapper">
        <video 
          :src="getMediaUrl(playbackData.video_file)" 
          autoplay 
          muted 
          loop 
          controls 
          class="fullscreen-video"
        ></video>
      </div>

      <!-- Live Video Playlist -->
      <div v-else-if="playbackData.video_type === 'playlist' && currentLivePlaylistItem" class="video-wrapper">
        <iframe 
          v-if="currentLivePlaylistItem.video_type === 'youtube'"
          :src="getYoutubeEmbedUrl(currentLivePlaylistItem.youtube_url)"
          frameborder="0"
          allow="autoplay"
          class="fullscreen-iframe"
        ></iframe>
        <video 
          v-else
          :src="getMediaUrl(currentLivePlaylistItem.video_file)" 
          autoplay 
          muted 
          @ended="nextLivePlaylistItem" 
          class="fullscreen-video"
        ></video>
      </div>
    </div>

    <!-- 3. REGULAR PLAYLIST DISPLAY (PRIORITY 3) -->
    <div v-else-if="playType === 'regular_playlist' && currentRegularItem" class="screen regular-screen">
      <div class="media-wrapper">
        <img 
          v-if="isImage(getItemPath(currentRegularItem))" 
          :src="getMediaUrl(getItemPath(currentRegularItem))" 
          class="media-element" 
        />
        <video 
          v-else-if="isVideo(getItemPath(currentRegularItem))" 
          :src="getMediaUrl(getItemPath(currentRegularItem))" 
          autoplay 
          muted 
          @ended="nextRegularItem" 
          class="media-element"
        ></video>
      </div>
    </div>

    <!-- 4. NO ACTIVE CONTENT / STANDBY SCREEN -->
    <div v-else class="screen standby-screen">
      <div class="standby-card">
        <i class="ni ni-tv-2 standby-icon"></i>
        <h2>Branch Standby</h2>
        <p>No active content schedule for Branch ID: {{ branchId || 'Unset' }}</p>
        <span class="pulse-dot"></span> Synchronized with Server (Real-time)
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { io } from "socket.io-client";

export default {
  name: "ActivePlaybackPlayer",
  data() {
    return {
      branchId: null,
      manualBranchId: null,
      branchesList: [],
      branchesLoading: false,

      playType: 'none',
      playbackData: null,
      slideTimer: null,
      socket: null,

      emergencyIndex: 0,
      livePlaylistIndex: 0,
      regularIndex: 0,

      defaultDuration: 10,
      apiBaseUrl: (process.env.VUE_APP_API_BASE_URL || '').replace(/\/api$/, '')
    };
  },
  computed: {
    currentEmergencyItem() {
      if (!this.playbackData) return null;
      // In your screenshot, the key is 'playlist_items' or 'mediaurl_playlist'
      const items = this.playbackData.playlist_items || this.playbackData.mediaurl_playlist || [];
      return items.length > 0 ? items[this.emergencyIndex] : null;
    },
    currentLivePlaylistItem() {
      if (!this.playbackData || !this.playbackData.playlist_video_details) return null;
      const details = this.playbackData.playlist_video_details;
      return details.length > 0 ? details[this.livePlaylistIndex] : null;
    },
    currentRegularItem() {
      if (!this.playbackData || !this.playbackData.playlist_items) return null;
      const items = this.playbackData.playlist_items;
      return items.length > 0 ? items[this.regularIndex] : null;
    }
  },
  mounted() {
    this.branchId = Number(this.$route.query.branch_id || localStorage.getItem('selectedBranchId') || 1);
    this.manualBranchId = this.branchId;
    this.fetchBranches();
    this.fetchActiveContent();
    this.initSocket();
  },
  beforeUnmount() {
    if (this.slideTimer) clearTimeout(this.slideTimer);
    if (this.socket) this.socket.disconnect();
  },
  methods: {
    initSocket() {
      this.socket = io(this.apiBaseUrl);
      this.socket.on("connect", () => {
        this.socket.emit("join_branch", this.branchId);
      });
      this.socket.on("CONTENT_UPDATE", (payload) => {
        this.processPlaybackResponse(payload);
      });
    },

    async fetchBranches() {
      this.branchesLoading = true;
      try {
        const res = await axios.get(`${this.apiBaseUrl}/api/v1/admin/branches`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        this.branchesList = res.data;
      } catch (e) {
        console.error("Error fetching branches:", e);
      }
      this.branchesLoading = false;
    },

    async fetchActiveContent() {
      if (!this.branchId) return;
      try {
        const response = await axios.get(`${this.apiBaseUrl}/api/v1/admin/device/active-playback?branch_id=${this.branchId}`);
        if (response.data && response.data.success) {
          this.processPlaybackResponse(response.data);
        }
      } catch (error) {
        console.error("Error fetching initial branch playback:", error);
      }
    },

    processPlaybackResponse(response) {
      const hasDataChanged = JSON.stringify(this.playbackData) !== JSON.stringify(response.data);
      const hasTypeChanged = this.playType !== response.play_type;

      if (hasDataChanged || hasTypeChanged) {
        this.playType = response.play_type;
        this.playbackData = response.data;
        this.emergencyIndex = 0;
        this.livePlaylistIndex = 0;
        this.regularIndex = 0;
        this.scheduleNextSlide();
      }
    },

    /**
     * FIX: Updated to handle the 'media_url' key found in your screenshot
     */
    getItemPath(item) {
      if (!item) return '';
      if (typeof item === 'string') return item;
      // Added item.media_url check
      return item.media_url || item.url || item.file_path || item.file || '';
    },

    getMediaUrl(path) {
      if (!path || typeof path !== 'string') return '';
      // If the path is already a full URL (like in your screenshot), return it
      if (path.startsWith('http') || path.startsWith('data:')) return path;
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;
      return `${this.apiBaseUrl}/${cleanPath}`;
    },

    onBranchChange() {
      this.manualBranchId = this.branchId;
      localStorage.setItem('selectedBranchId', this.branchId);
      if (this.socket) this.socket.emit("join_branch", this.branchId);
      this.fetchActiveContent();
    },

    applyManualBranchId() {
      if (this.manualBranchId && this.manualBranchId > 0) {
        this.branchId = Number(this.manualBranchId);
        this.onBranchChange();
      }
    },

    scheduleNextSlide() {
      if (this.slideTimer) clearTimeout(this.slideTimer);

      const currentItem = this.playType === 'emergency_broadcast' ? this.currentEmergencyItem : this.currentRegularItem;
      if (!currentItem) return;

      const path = this.getItemPath(currentItem);
      if (this.isImage(path)) {
        const duration = (currentItem.display_time || this.playbackData.display_time || this.defaultDuration) * 1000;
        this.slideTimer = setTimeout(() => {
          if (this.playType === 'emergency_broadcast') this.nextEmergencyItem();
          else this.nextRegularItem();
        }, duration);
      }
    },

    nextRegularItem() {
      if (!this.playbackData?.playlist_items) return;
      const total = this.playbackData.playlist_items.length;
      if (total > 0) {
        this.regularIndex = (this.regularIndex + 1) % total;
        this.scheduleNextSlide();
      }
    },

    nextEmergencyItem() {
      const items = this.playbackData?.playlist_items || this.playbackData?.mediaurl_playlist || [];
      if (items.length > 0) {
        this.emergencyIndex = (this.emergencyIndex + 1) % items.length;
        this.scheduleNextSlide();
      }
    },

    nextLivePlaylistItem() {
      const details = this.playbackData?.playlist_video_details || [];
      if (details.length > 0) {
        this.livePlaylistIndex = (this.livePlaylistIndex + 1) % details.length;
      }
    },

    isImage(url) {
      if (!url) return false;
      return /\.(jpeg|jpg|gif|png|webp|svg)$/i.test(url);
    },

    isVideo(url) {
      if (!url) return false;
      return /\.(mp4|webm|ogg|m4v|mov)$/i.test(url);
    },

    getYoutubeEmbedUrl(url) {
      if (!url || typeof url !== 'string') return '';
      let videoId = '';
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        videoId = match[2];
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&playlist=${videoId}&loop=1`;
      }
      return '';
    },

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(err => console.error(err));
      } else {
        if (document.exitFullscreen) document.exitFullscreen();
      }
    }
  }
};
</script>

<style scoped>
/* STYLES PRESERVED */
.playback-container { width: 100%; height: 100vh; background-color: #0b1426; color: #fff; position: relative; overflow: hidden; font-family: 'Open Sans', sans-serif; }
.top-control-bar { position: absolute; top: 15px; right: 25px; display: flex; align-items: center; gap: 15px; z-index: 2000; background: rgba(15, 23, 42, 0.85); padding: 8px 16px; border-radius: 12px; backdrop-filter: blur(8px); border: 1px solid rgba(255, 255, 255, 0.1); box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4); }
.device-selector-wrapper, .manual-input-wrapper { display: flex; align-items: center; gap: 8px; }
.selector-label { font-size: 0.85rem; font-weight: 600; color: #e2e8f0; margin: 0; white-space: nowrap; }
.device-select-dropdown { background-color: #1e293b; color: #ffffff; border: 1px solid #334155; border-radius: 8px; padding: 6px 12px; font-size: 0.85rem; font-weight: 500; outline: none; cursor: pointer; }
.manual-id-input { width: 65px; background-color: #1e293b; color: #ffffff; border: 1px solid #334155; border-radius: 6px; padding: 5px 8px; font-size: 0.85rem; outline: none; text-align: center; }
.btn-apply-id { background: #38bdf8; color: #0f172a; border: none; font-weight: 700; padding: 5px 10px; border-radius: 6px; font-size: 0.8rem; cursor: pointer; }
.btn-fullscreen { background: #334155; border: none; color: #fff; padding: 5px 12px; border-radius: 6px; font-size: 0.8rem; cursor: pointer; }
.screen { width: 100%; height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center; }
.emergency-screen { background: linear-gradient(135deg, #800000 0%, #1a0000 100%); }
.emergency-banner { background-color: #dc3545; width: 100%; padding: 15px 0; text-align: center; display: flex; align-items: center; justify-content: center; gap: 15px; }
.emergency-banner h1 { color: #ffffff; margin: 0; font-weight: 800; }
.emergency-content { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 20px; text-align: center; }
.emergency-title { font-size: 2.5rem; color: #ffc107; margin-bottom: 15px; }
.emergency-message { font-size: 1.8rem; color: #ffffff; }
.live-badge { position: absolute; top: 75px; left: 20px; background: rgba(220, 53, 69, 0.9); color: #fff; padding: 8px 16px; border-radius: 20px; font-weight: bold; z-index: 100; }
.media-wrapper, .video-wrapper { width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; }
.media-element, .fullscreen-video { max-width: 100%; max-height: 100%; object-fit: contain; }
.fullscreen-iframe { width: 100vw; height: 100vh; border: none; }
.standby-card { text-align: center; background: #152238; padding: 40px 60px; border-radius: 16px; border: 1px solid rgba(255, 255, 255, 0.05); }
.standby-icon { font-size: 4rem; color: #475569; margin-bottom: 15px; }
.pulse-dot { display: inline-block; width: 10px; height: 10px; background-color: #10b981; border-radius: 50%; margin-right: 5px; animation: pulse 1.5s infinite; }
@keyframes pulse { 0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); } 70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); } 100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); } }
</style>